import { Activity, CircleDot, LayoutGrid, LogOut, MessageSquare, Radio, Settings, Shield, SlidersHorizontal, Users, Wifi, WifiOff } from 'lucide-react';
import { session } from '../lib/session';
import { updateSettings, useStore, type Drawer } from '../store';

function MicMeter() {
  const db = useStore((s) => s.micLevelDb);
  const media = useStore((s) => s.media);
  const segs = 16;
  // Map −60..0 dBFS onto 16 segments; top 2 orange, last red.
  const lit = media === 'ready' ? Math.round(((Math.max(-60, Math.min(0, db)) + 60) / 60) * segs) : 0;
  return (
    <div className="meter" title="Microphone level">
      <span>MIC</span>
      <div className="meter__bar">
        {Array.from({ length: segs }, (_, i) => (
          <span key={i} className={`meter__seg ${i < lit ? (i >= segs - 1 ? 'meter__seg--clip' : i >= segs - 3 ? 'meter__seg--hot' : 'meter__seg--on') : ''}`} />
        ))}
      </div>
      <span className="meter__val">{media === 'ready' ? `${Math.max(-60, db).toFixed(0)} dB` : media === 'nomic' ? 'NO MIC' : '—'}</span>
    </div>
  );
}

export function Header() {
  const conn = useStore((s) => s.conn);
  const rtt = useStore((s) => s.rttMs);
  const user = useStore((s) => s.user);
  const serverName = useStore((s) => s.serverName);
  const drawer = useStore((s) => s.drawer);
  const view = useStore((s) => s.view);
  const unread = useStore((s) => Object.values(s.unread).reduce((a, b) => a + b, 0));
  const feeding = useStore((s) => s.feeding.length);
  const layout = useStore((s) => s.settings.layout);

  const toggle = (d: Drawer) => useStore.setState((s) => ({ drawer: s.drawer === d ? 'none' : d }));

  return (
    <header className="header">
      <div className="header__brand">
        <img className="header__logo" src="/brand/11-compact-dark.svg" alt="BCComms" />
        <span className="eyebrow">{serverName}</span>
      </div>
      <div className="header__status">
        <span className={`chip ${conn === 'online' ? 'chip--success' : conn === 'reconnecting' ? 'chip--warning' : 'chip--error'}`}>
          {conn === 'online' ? <Wifi size={12} /> : <WifiOff size={12} />}
          {conn === 'online' ? 'ONLINE' : conn === 'reconnecting' ? 'RECONNECTING' : conn.toUpperCase()}
        </span>
        {rtt !== null && (
          <span className="chip chip--mono">
            <Activity size={12} /> {rtt} ms
          </span>
        )}
        <MicMeter />
      </div>
      <div className="header__spacer" />
      <div className="header__actions">
        {user?.canFeedProgram && (
          <button className={`iconbtn ${drawer === 'program' || feeding ? 'iconbtn--active' : ''}`} aria-label="Program feed" title="Program feed (IFB source)" onClick={() => toggle('program')}>
            <Radio size={20} />
            {feeding > 0 && <span className="iconbtn__badge">{feeding}</span>}
          </button>
        )}
        <button className="iconbtn" aria-label={layout === 'dial' ? 'Switch to grid' : 'Switch to dial'} title={layout === 'dial' ? 'Grid view' : 'Dial view'} onClick={() => updateSettings({ layout: layout === 'dial' ? 'grid' : 'dial' })}>
          {layout === 'dial' ? <LayoutGrid size={20} /> : <CircleDot size={20} />}
        </button>
        <button className={`iconbtn ${drawer === 'mixer' ? 'iconbtn--active' : ''}`} aria-label="Mixer" title="Mixer (listen levels)" onClick={() => toggle('mixer')}>
          <SlidersHorizontal size={20} />
        </button>
        <button className={`iconbtn ${drawer === 'people' ? 'iconbtn--active' : ''}`} aria-label="People" title="People" onClick={() => toggle('people')}>
          <Users size={20} />
        </button>
        <button className={`iconbtn ${drawer === 'chat' ? 'iconbtn--active' : ''}`} aria-label="Messages" title="Messages" onClick={() => (drawer === 'chat' ? toggle('chat') : session.openChat(null))}>
          <MessageSquare size={20} />
          {unread > 0 && <span className="iconbtn__badge">{unread}</span>}
        </button>
        <button className={`iconbtn ${drawer === 'settings' ? 'iconbtn--active' : ''}`} aria-label="Settings" title="Settings" onClick={() => toggle('settings')}>
          <Settings size={20} />
        </button>
        {user?.role === 'admin' && (
          <button className={`iconbtn ${view === 'admin' ? 'iconbtn--active' : ''}`} aria-label="Admin" title="Server admin" onClick={() => useStore.setState({ view: view === 'admin' ? 'panel' : 'admin', drawer: 'none' })}>
            <Shield size={20} />
          </button>
        )}
        <button className="iconbtn" aria-label="Sign out" title={`Sign out ${user?.displayName ?? ''}`} onClick={() => session.logout()}>
          <LogOut size={20} />
        </button>
      </div>
    </header>
  );
}
